import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, GraduationCap, Heart, Mail, Calendar, CalendarPlus, Shield, Youtube, Save, Trash2, Pencil, X, Plus, MessageSquare, RefreshCw, CheckCircle2, FolderOpen, Calculator, LayoutGrid, PanelLeftClose, PanelLeftOpen, ChevronRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api, API_BASE_URL } from "../lib/api";
import { Link } from "react-router-dom";
import MessagingCenter from "../components/admin/MessagingCenter";
import AdminDocumentsManager from "../components/admin/AdminDocumentsManager";
import BudgetEstimator from "../components/admin/BudgetEstimator";

const tabs = [
  { k: "documents", icon: FolderOpen },
  { k: "estimates", icon: Calculator },
  { k: "talent", icon: GraduationCap },
  { k: "couples", icon: Heart },
  { k: "inquiries", icon: Mail },
  { k: "bookings", icon: Calendar },
  { k: "events", icon: CalendarPlus },
  { k: "messages", icon: MessageSquare },
  { k: "settings", icon: Youtube },
];

const EMPTY_EVENT = {
  title_en: "",
  title_fr: "",
  description_en: "",
  description_fr: "",
  date: "",
  duration_minutes: 90,
  kind: "webinar",
  capacity: 100,
  price: 0,
  meeting_link: "",
};

export default function Admin() {
  const { t, lang } = useLang();
  const { user, loading } = useAuth();
  const [active, setActive] = useState("talent");
  const [talent, setTalent] = useState([]);
  const [talentStats, setTalentStats] = useState(null);
  const [couples, setCouples] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null); // null | 'new' | eventObj
  const [eventForm, setEventForm] = useState(EMPTY_EVENT);
  const [eventSaving, setEventSaving] = useState(false);
  const [settings, setSettings] = useState({ homepage_video_en: "", homepage_video_fr: "" });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [irccSyncing, setIrccSyncing] = useState(false);
  const [irccStatus, setIrccStatus] = useState(null);
  const [irccSyncResult, setIrccSyncResult] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [messageTarget, setMessageTarget] = useState(null);
  const [navLayout, setNavLayout] = useState(() => {
    try {
      return localStorage.getItem("jnic_admin_nav_layout") || "sidebar";
    } catch {
      return "sidebar";
    }
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const toggleNavLayout = (layout) => {
    setNavLayout(layout);
    try {
      localStorage.setItem("jnic_admin_nav_layout", layout);
    } catch {}
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    setFetching(true);
    Promise.all([
      api.get("/registry/talent").then((r) => setTalent(r.data)).catch(() => {}),
      api.get("/registry/talent/stats").then((r) => setTalentStats(r.data)).catch(() => {}),
      api.get("/registry/couples").then((r) => setCouples(r.data)).catch(() => {}),
      api.get("/inquiries").then((r) => setInquiries(r.data)).catch(() => {}),
      api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {}),
      api.get("/events").then((r) => setEvents(r.data)).catch(() => {}),
      api.get("/express-entry/sync-status").then((r) => setIrccStatus(r.data)).catch(() => {}),
      api.get("/settings").then((r) => setSettings({
        homepage_video_en: r.data.homepage_video_en || "",
        homepage_video_fr: r.data.homepage_video_fr || "",
      })).catch(() => {}),
    ]).finally(() => setFetching(false));
  }, [user]);

  const syncIRCC = async () => {
    setIrccSyncing(true);
    setIrccSyncResult(null);
    try {
      const res = await api.post("/express-entry/sync");
      setIrccSyncResult(res.data);
      const statusRes = await api.get("/express-entry/sync-status");
      setIrccStatus(statusRes.data);
    } catch (e) {
      alert(e.response?.data?.detail || t("common.error"));
    } finally {
      setIrccSyncing(false);
    }
  };

  const saveSettings = async () => {
    setSettingsSaving(true);
    setSettingsSaved(false);
    try {
      const r = await api.put("/settings", settings);
      setSettings({
        homepage_video_en: r.data.homepage_video_en || "",
        homepage_video_fr: r.data.homepage_video_fr || "",
      });
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2500);
    } catch (e) {
      alert(e.response?.data?.detail || t("common.error"));
    } finally {
      setSettingsSaving(false);
    }
  };

  const openNewEvent = () => {
    setEditingEvent("new");
    setEventForm({ ...EMPTY_EVENT });
  };
  const openEditEvent = (ev) => {
    setEditingEvent(ev);
    // convert ISO date to "YYYY-MM-DDTHH:mm" for datetime-local input
    const iso = ev.date || "";
    const local = iso ? new Date(iso).toISOString().slice(0, 16) : "";
    setEventForm({
      title_en: ev.title_en || "",
      title_fr: ev.title_fr || "",
      description_en: ev.description_en || "",
      description_fr: ev.description_fr || "",
      date: local,
      duration_minutes: ev.duration_minutes || 90,
      kind: ev.kind || "webinar",
      capacity: ev.capacity || 100,
      price: ev.price ?? 0,
      meeting_link: ev.meeting_link || "",
    });
  };
  const closeEventEditor = () => {
    setEditingEvent(null);
    setEventForm(EMPTY_EVENT);
  };
  const saveEvent = async () => {
    if (!eventForm.title_en || !eventForm.title_fr || !eventForm.date) {
      alert(lang === "fr" ? "Titres FR + EN et date sont requis." : "FR + EN titles and date are required.");
      return;
    }
    setEventSaving(true);
    try {
      const payload = {
        ...eventForm,
        date: new Date(eventForm.date).toISOString(),
        capacity: Number(eventForm.capacity) || 100,
        duration_minutes: Number(eventForm.duration_minutes) || 90,
        price: Number(eventForm.price) || 0,
        meeting_link: eventForm.meeting_link || null,
      };
      if (editingEvent === "new") {
        await api.post("/events", payload);
      } else {
        await api.put(`/events/${editingEvent.event_id}`, payload);
      }
      const r = await api.get("/events");
      setEvents(r.data);
      closeEventEditor();
    } catch (e) {
      alert(e.response?.data?.detail || t("common.error"));
    } finally {
      setEventSaving(false);
    }
  };
  const deleteEvent = async (ev) => {
    if (!window.confirm(lang === "fr" ? `Supprimer l'événement "${ev.title_fr}" ?` : `Delete event "${ev.title_en}"?`)) return;
    try {
      await api.delete(`/events/${ev.event_id}`);
      const r = await api.get("/events");
      setEvents(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || t("common.error"));
    }
  };

  const seedJnicLaunchEvent = async () => {
    if (!window.confirm(lang === "fr"
      ? "Créer/mettre à jour l'événement du 26 juillet 2026 (17h Cameroun / 12h Toronto) ? Ceci supprimera aussi les anciens événements « Express Entry »."
      : "Create/update the July 26, 2026 event? Legacy 'Express Entry' events will be removed."
    )) return;
    try {
      const r = await api.post("/admin/events/seed-jnic-launch");
      const events2 = await api.get("/events");
      setEvents(events2.data);
      alert(lang === "fr"
        ? `✓ Événement ${r.data.action === "created" ? "créé" : "mis à jour"}. ${r.data.legacy_events_removed} ancien(s) événement(s) supprimé(s).`
        : `✓ Event ${r.data.action}. ${r.data.legacy_events_removed} legacy event(s) removed.`);
    } catch (e) {
      alert(e.response?.data?.detail || t("common.error"));
    }
  };

  const openMessage = (email, name) => {
    if (!email) return;
    setMessageTarget({ email, name, key: Date.now() });
    setActive("messages");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading) return <div className="max-w-[1400px] mx-auto px-6 py-32 text-navy/60">{t("common.loading")}</div>;

  if (!user) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="admin-login-gate">
        <div className="text-center">
          <Shield size={48} className="mx-auto text-terracotta mb-4" />
          <p className="text-navy/70 mb-6">{lang === "fr" ? "Connectez-vous pour accéder à l'admin." : "Sign in to access admin."}</p>
          <Link to="/login?redirect=/admin" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-alabaster text-sm hover:bg-terracotta transition">{t("nav.login")}</Link>
        </div>
      </div>
    );
  }

  if (user.role !== "admin") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="admin-forbidden">
        <div className="text-center">
          <Shield size={48} className="mx-auto text-terracotta mb-4" />
          <p className="font-serif text-3xl text-navy mb-2">{t("admin.forbidden")}</p>
          <p className="text-navy/60 text-sm">{lang === "fr" ? "Votre compte n'a pas les droits d'administrateur." : "Your account doesn't have admin privileges."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-alabaster min-h-[80vh]" data-testid="admin-page">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-2">— {t("admin.title")}</p>
            <h1 className="font-serif text-4xl lg:text-5xl text-navy tracking-tight leading-[0.95]">
              {lang === "fr" ? "Le tableau de bord." : "The dashboard."}
            </h1>
          </div>

          {/* Disposition toggle (Barre latérale vs Barre supérieure) */}
          <div className="flex items-center gap-1.5 p-1 bg-white border border-navy/10 rounded-xl shadow-xs text-xs">
            <span className="text-[11px] font-medium text-navy/50 px-2 select-none">
              {lang === "fr" ? "Menu :" : "Menu:"}
            </span>
            <button
              onClick={() => toggleNavLayout("sidebar")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium transition ${
                navLayout === "sidebar"
                  ? "bg-navy text-alabaster shadow-xs"
                  : "text-navy/70 hover:bg-navy/5"
              }`}
              title={lang === "fr" ? "Afficher les onglets sur le côté (barre latérale)" : "Show tabs on the side"}
            >
              <PanelLeftClose size={13} />
              <span>{lang === "fr" ? "Sur le côté (Latéral)" : "Sidebar"}</span>
            </button>
            <button
              onClick={() => toggleNavLayout("top")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium transition ${
                navLayout === "top"
                  ? "bg-navy text-alabaster shadow-xs"
                  : "text-navy/70 hover:bg-navy/5"
              }`}
              title={lang === "fr" ? "Afficher les onglets en haut (horizontal)" : "Show tabs on top"}
            >
              <LayoutGrid size={13} />
              <span>{lang === "fr" ? "En haut" : "Top bar"}</span>
            </button>
          </div>
        </div>

        {talentStats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8">
            {[
              { label: t("admin.stat_total"), val: talentStats.total, accent: "text-terracotta" },
              { label: t("admin.stat_passport"), val: talentStats.with_passport, accent: "text-gold" },
              { label: t("admin.stat_bachelor"), val: talentStats.by_education.bachelor, accent: "text-navy" },
              { label: t("admin.stat_master"), val: talentStats.by_education.master, accent: "text-navy" },
              { label: t("admin.stat_phd"), val: talentStats.by_education.phd, accent: "text-navy" },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-navy/8 p-4 lg:p-5" data-testid={`stat-${i}`}>
                <p className={`font-serif text-3xl lg:text-4xl leading-none ${s.accent}`}>{s.val}</p>
                <p className="text-[10px] tracking-[0.25em] uppercase text-navy/60 mt-2">{s.label}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        {/* HORIZONTAL TABS (Si navLayout === "top") */}
        {navLayout === "top" && (
          <div className="flex flex-wrap gap-2 border-b border-navy/10 mb-6 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const count = tab.k === "talent" ? talent.length
                : tab.k === "couples" ? couples.length
                : tab.k === "inquiries" ? inquiries.length
                : tab.k === "bookings" ? bookings.length
                : tab.k === "events" ? events.length
                : null;
              return (
                <button
                  key={tab.k}
                  onClick={() => setActive(tab.k)}
                  data-testid={`admin-tab-${tab.k}`}
                  className={`inline-flex items-center gap-2 px-4 py-3 text-sm transition border-b-2 -mb-px whitespace-nowrap ${
                    active === tab.k
                      ? "border-terracotta text-navy font-semibold"
                      : "border-transparent text-navy/50 hover:text-navy"
                  }`}
                >
                  <Icon size={15} />
                  {t(`admin.tab_${tab.k}`)}
                  {count !== null && <span className="ml-1 text-xs text-navy/40">({count})</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* CONTENEUR AVEC SIDEBAR (Si navLayout === "sidebar") */}
        <div className={navLayout === "sidebar" ? "flex flex-col lg:flex-row gap-6 items-start" : ""}>
          {navLayout === "sidebar" && (
            <aside
              className={`w-full ${
                sidebarCollapsed ? "lg:w-20" : "lg:w-72"
              } shrink-0 bg-white border border-navy/10 rounded-2xl p-3 shadow-xs lg:sticky lg:top-24 transition-all duration-200 z-10`}
            >
              <div className="flex items-center justify-between px-2 py-1.5 mb-2 border-b border-navy/5">
                {!sidebarCollapsed && (
                  <p className="text-[10px] uppercase font-bold tracking-wider text-navy/40">
                    {lang === "fr" ? "Navigation Admin" : "Admin Navigation"}
                  </p>
                )}
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="hidden lg:inline-flex p-1 rounded-md text-navy/50 hover:text-navy hover:bg-navy/5 ml-auto"
                  title={sidebarCollapsed ? (lang === "fr" ? "Développer le menu" : "Expand") : (lang === "fr" ? "Réduire le menu" : "Collapse")}
                >
                  {sidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
                </button>
              </div>

              <nav className="flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible pb-1 lg:pb-0">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const count = tab.k === "talent" ? talent.length
                    : tab.k === "couples" ? couples.length
                    : tab.k === "inquiries" ? inquiries.length
                    : tab.k === "bookings" ? bookings.length
                    : tab.k === "events" ? events.length
                    : null;
                  const isActive = active === tab.k;

                  return (
                    <button
                      key={tab.k}
                      onClick={() => setActive(tab.k)}
                      data-testid={`admin-tab-side-${tab.k}`}
                      title={t(`admin.tab_${tab.k}`)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs sm:text-sm font-medium transition text-left shrink-0 lg:shrink w-auto lg:w-full ${
                        isActive
                          ? "bg-navy text-alabaster shadow-xs"
                          : "text-navy/70 hover:text-navy hover:bg-navy/5"
                      }`}
                    >
                      <span className={`p-1.5 rounded-lg ${isActive ? "bg-white/10 text-alabaster" : "text-navy/60"}`}>
                        <Icon size={16} />
                      </span>

                      {!sidebarCollapsed && (
                        <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                          <span className="truncate">{t(`admin.tab_${tab.k}`)}</span>
                          {count !== null && (
                            <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${
                              isActive ? "bg-white/20 text-alabaster" : "bg-navy/5 text-navy/60"
                            }`}>
                              {count}
                            </span>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </nav>

              {!sidebarCollapsed && (
                <div className="mt-4 pt-3 border-t border-navy/5 px-2 hidden lg:block text-[11px] text-navy/40">
                  <span>{lang === "fr" ? "💡 Astuce : basculez en mode « En haut » si vous préférez la vue classique." : "Tip: Switch to 'Top' if you prefer horizontal tabs."}</span>
                </div>
              )}
            </aside>
          )}

          {/* MAIN CONTENT AREA */}
          <div className={navLayout === "sidebar" ? "flex-1 min-w-0 w-full" : "w-full"}>
            {fetching && <p className="text-navy/60 mb-4">{t("common.loading")}</p>}

            {/* DOCUMENTS */}
            {active === "documents" && (
              <AdminDocumentsManager />
            )}

            {/* ESTIMATES & DEVIS GENERATOR */}
            {active === "estimates" && (
              <BudgetEstimator onOpenMessage={openMessage} />
            )}

        {/* TALENT */}
        {active === "talent" && (
          <div className="space-y-4" data-testid="admin-talent-section">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <p className="text-xs text-navy/50">{lang === "fr" ? `${talent.length} profil${talent.length > 1 ? "s" : ""}` : `${talent.length} profile${talent.length !== 1 ? "s" : ""}`}</p>
              <a
                href={`${API_BASE_URL}/registry/talent/export.csv`}
                target="_blank"
                rel="noreferrer"
                data-testid="admin-talent-export-csv"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta text-alabaster text-xs hover:bg-navy transition"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v12m0 0l-4-4m4 4l4-4M4 21h16"/></svg>
                {lang === "fr" ? "Exporter en CSV" : "Export CSV"}
              </a>
            </div>
            <div className="bg-white border border-navy/8 rounded-2xl overflow-hidden" data-testid="admin-talent-list">
            {talent.length === 0 ? (
              <p className="p-8 text-navy/50 italic">{t("admin.empty")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-cream text-navy/70 text-[10px] tracking-[0.2em] uppercase">
                    <tr>
                      <th className="text-left px-4 py-3">{lang === "fr" ? "Nom" : "Name"}</th>
                      <th className="text-left px-4 py-3">{lang === "fr" ? "Pays" : "Country"}</th>
                      <th className="text-left px-4 py-3">{lang === "fr" ? "Niveau d'études" : "Education"}</th>
                      <th className="text-left px-4 py-3">{lang === "fr" ? "Domaine" : "Field"}</th>
                      <th className="text-left px-4 py-3">{lang === "fr" ? "Exp." : "YoE"}</th>
                      <th className="text-left px-4 py-3">{lang === "fr" ? "Passeport" : "Passport"}</th>
                      <th className="text-left px-4 py-3">{lang === "fr" ? "Finances" : "Fin"}</th>
                      <th className="text-left px-4 py-3">Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {talent.map((r) => (
                      <tr key={r.talent_id} className="border-t border-navy/5 hover:bg-cream/40" data-testid={`talent-row-${r.talent_id}`}>
                        <td className="px-4 py-3 text-navy font-medium">{r.first_name} {r.last_name}</td>
                        <td className="px-4 py-3 text-navy/70">{r.country_of_residence}{r.city ? `, ${r.city}` : ""}</td>
                        <td className="px-4 py-3 text-navy/70 capitalize">{r.education_level}</td>
                        <td className="px-4 py-3 text-navy/70">{r.field_of_study || "—"}</td>
                        <td className="px-4 py-3 text-navy/70">{r.years_of_experience}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full ${r.has_passport ? "bg-cream text-terracotta" : "bg-navy/5 text-navy/60"}`}>
                            {r.has_passport ? (lang === "fr" ? "oui" : "yes") : (lang === "fr" ? "non" : "no")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-navy/70 text-xs">{r.financial_situation}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => openMessage(r.email, `${r.first_name} ${r.last_name}`)} className="text-terracotta hover:underline text-xs text-left">{r.email}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          </div>
        )}

        {/* COUPLES */}
        {active === "couples" && (
          <div className="space-y-3" data-testid="admin-couples-list">
            {couples.length === 0 ? (
              <p className="bg-white border border-navy/8 rounded-2xl p-8 text-navy/50 italic">{t("admin.empty")}</p>
            ) : (
              couples.map((c) => (
                <motion.div
                  key={c.couple_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white border border-navy/8 rounded-2xl p-6"
                  data-testid={`couple-row-${c.couple_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[10px] tracking-[0.2em] uppercase text-terracotta mb-1">{c.relationship_status.replace("_", " ")}</p>
                      <p className="font-serif text-2xl text-navy leading-tight">
                        {c.a_first_name} {c.a_last_name}
                        <span className="text-terracotta mx-3">×</span>
                        {c.b_first_name} {c.b_last_name}
                      </p>
                      <p className="text-xs text-navy/60 mt-2">
                        {c.a_nationality} ({c.a_current_country}) × {c.b_nationality} ({c.b_current_country})
                      </p>
                    </div>
                    <div className="text-right">
                      <a href={`mailto:${c.a_email}`} className="text-xs text-terracotta hover:underline">{c.a_email}</a>
                      <p className="text-[10px] text-navy/40 mt-1">{new Date(c.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-navy/5 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div><p className="text-navy/40 uppercase tracking-[0.15em] text-[9px] mb-1">{lang === "fr" ? "Parrainage" : "Sponsorship"}</p><p className="text-navy/80">{c.sponsorship_type}</p></div>
                    <div><p className="text-navy/40 uppercase tracking-[0.15em] text-[9px] mb-1">{lang === "fr" ? "Cohabitation" : "Cohab"}</p><p className="text-navy/80">{c.cohabitation_start_date || "—"}</p></div>
                    <div><p className="text-navy/40 uppercase tracking-[0.15em] text-[9px] mb-1">{lang === "fr" ? "Enfants" : "Children"}</p><p className="text-navy/80">{c.children ? c.number_of_children : "—"}</p></div>
                    <div><p className="text-navy/40 uppercase tracking-[0.15em] text-[9px] mb-1">Docs</p><p className="text-navy/80">{(c.documents_ready || []).length}/5</p></div>
                  </div>
                  {c.notes && <p className="mt-4 pt-4 border-t border-navy/5 text-sm text-navy/70 italic">&ldquo;{c.notes}&rdquo;</p>}
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* INQUIRIES */}
        {active === "inquiries" && (
          <div className="bg-white border border-navy/8 rounded-2xl overflow-hidden" data-testid="admin-inquiries-list">
            {inquiries.length === 0 ? (
              <p className="p-8 text-navy/50 italic">{t("admin.empty")}</p>
            ) : (
              <div className="divide-y divide-navy/5">
                {inquiries.map((q) => (
                  <div key={q.inquiry_id} className="p-6 flex flex-wrap items-start gap-6" data-testid={`inquiry-row-${q.inquiry_id}`}>
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-serif text-xl text-navy">{q.first_name} {q.last_name}</p>
                      <p className="text-xs text-navy/60 mt-1">{q.email} {q.phone && `· ${q.phone}`}</p>
                      <button onClick={() => openMessage(q.email, `${q.first_name} ${q.last_name}`)} className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[.15em] text-terracotta hover:text-navy"><Mail size={11}/>{lang === "fr" ? "Écrire" : "Write"}</button>
                    </div>
                    <div>
                      <span className="text-[10px] tracking-[0.2em] uppercase bg-cream text-terracotta px-3 py-1 rounded-full">{q.service}</span>
                    </div>
                    <div className="flex-1 min-w-[200px] text-sm text-navy/70">{q.message || "—"}</div>
                    <div className="text-xs text-navy/40">{new Date(q.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BOOKINGS */}
        {active === "bookings" && (
          <div className="bg-white border border-navy/8 rounded-2xl overflow-hidden" data-testid="admin-bookings-list">
            {bookings.length === 0 ? (
              <p className="p-8 text-navy/50 italic">{t("admin.empty")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-cream text-navy/70 text-[10px] tracking-[0.2em] uppercase">
                    <tr>
                      <th className="text-left px-4 py-3">{lang === "fr" ? "Date (ET)" : "Date (ET)"}</th>
                      <th className="text-left px-4 py-3">{lang === "fr" ? "Nom" : "Name"}</th>
                      <th className="text-left px-4 py-3">Email</th>
                      <th className="text-left px-4 py-3">{lang === "fr" ? "Téléphone" : "Phone"}</th>
                      <th className="text-left px-4 py-3">{lang === "fr" ? "Sujet" : "Topic"}</th>
                      <th className="text-left px-4 py-3">Notes</th>
                      <th className="text-left px-4 py-3">{lang === "fr" ? "Statut" : "Status"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => {
                      const dt = new Date(b.slot_start);
                      const et = dt.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", {
                        weekday: "short", day: "numeric", month: "short",
                        hour: "2-digit", minute: "2-digit", timeZone: "America/Toronto", hour12: false,
                      });
                      return (
                        <tr key={b.booking_id} className="border-t border-navy/5 hover:bg-cream/40" data-testid={`booking-row-${b.booking_id}`}>
                          <td className="px-4 py-3 text-navy font-medium whitespace-nowrap">{et}</td>
                          <td className="px-4 py-3 text-navy/80">{b.full_name || b.user_name || "—"}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => openMessage(b.email || b.user_email, b.full_name || b.user_name || "Client JNIC")} className="text-terracotta hover:underline text-xs text-left">
                              {b.email || b.user_email}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-navy/70 text-xs">{b.phone || "—"}</td>
                          <td className="px-4 py-3 text-navy/80 text-xs max-w-[200px] truncate" title={b.topic}>{b.topic}</td>
                          <td className="px-4 py-3 text-navy/60 text-xs max-w-[240px] truncate" title={b.notes || ""}>{b.notes || "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full ${b.status === "confirmed" ? "bg-cream text-terracotta" : "bg-navy/5 text-navy/60"}`}>
                              {b.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {/* EVENTS */}
        {active === "events" && (
          <div className="space-y-4" data-testid="admin-events-panel">
            {/* Quick-seed banner — visible whenever the target event is missing */}
            {!events.some((ev) => ev.event_id === "evt_immigration_jul26") && (
              <div className="bg-cream border-2 border-terracotta/40 rounded-2xl p-6 flex flex-col lg:flex-row items-start lg:items-center gap-5" data-testid="admin-seed-banner">
                <span className="w-12 h-12 rounded-full bg-terracotta text-alabaster grid place-items-center shrink-0">
                  <CalendarPlus size={20} />
                </span>
                <div className="flex-1">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-1">
                    {lang === "fr" ? "Événement de lancement JNIC" : "JNIC launch event"}
                  </p>
                  <p className="font-serif text-lg text-navy leading-tight">
                    {lang === "fr"
                      ? "Créer en 1 clic l'événement « Immigration au Canada · Séance de questions » — Dimanche 26 juillet 2026 · 17h Cameroun / 12h Toronto"
                      : "One-click create the launch event — Sun July 26, 2026 · 5PM WAT / 12PM Toronto"}
                  </p>
                  <p className="text-navy/60 text-xs mt-1">
                    {lang === "fr"
                      ? "Aussi : supprime les anciens événements « Express Entry » qui pourraient traîner dans votre base."
                      : "Also removes any legacy 'Express Entry' events from your database."}
                  </p>
                </div>
                <button
                  onClick={seedJnicLaunchEvent}
                  data-testid="admin-seed-jnic-launch"
                  className="inline-flex items-center gap-2 bg-navy text-alabaster px-5 py-3 rounded-full text-sm hover:bg-terracotta transition whitespace-nowrap"
                >
                  <Plus size={14} />
                  {lang === "fr" ? "Créer maintenant" : "Create now"}
                </button>
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-sm text-navy/60">
                {lang === "fr"
                  ? "Gérez les événements publics affichés sur jnic.org/events."
                  : "Manage public events shown on jnic.org/events."}
              </p>
              <button
                onClick={openNewEvent}
                data-testid="admin-event-new"
                className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-navy text-alabaster hover:bg-terracotta text-sm transition"
              >
                <Plus size={14} />
                {lang === "fr" ? "Nouvel événement" : "New event"}
              </button>
            </div>

            {events.length === 0 ? (
              <div className="bg-white border border-navy/8 rounded-2xl p-8">
                <p className="text-navy/50 italic">{t("admin.empty")}</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {events.map((ev) => {
                  const dt = new Date(ev.date);
                  const et = dt.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", {
                    weekday: "long", day: "numeric", month: "long", year: "numeric",
                    hour: "2-digit", minute: "2-digit", timeZone: "America/Toronto", hour12: false,
                  });
                  const wat = dt.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", {
                    hour: "2-digit", minute: "2-digit", timeZone: "Africa/Douala", hour12: false,
                  });
                  return (
                    <div key={ev.event_id} className="bg-white border border-navy/8 rounded-2xl p-5 flex flex-col lg:flex-row lg:items-center gap-4" data-testid={`admin-event-${ev.event_id}`}>
                      <div className="flex-1">
                        <p className="text-[10px] tracking-[0.2em] uppercase text-terracotta">{ev.kind} · {ev.duration_minutes} min · {ev.price > 0 ? `${ev.price} CAD` : (lang === "fr" ? "gratuit" : "free")}</p>
                        <p className="font-serif text-xl text-navy mt-1">{lang === "fr" ? ev.title_fr : ev.title_en}</p>
                        <p className="text-navy/70 text-sm mt-1">{et}</p>
                        <p className="text-navy/50 text-xs">{lang === "fr" ? `Cameroun : ${wat}` : `Cameroon: ${wat}`} · <span className="text-terracotta">{ev.registrations || 0} / {ev.capacity} {lang === "fr" ? "inscrits" : "registered"}</span></p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditEvent(ev)}
                          data-testid={`admin-event-edit-${ev.event_id}`}
                          className="inline-flex items-center gap-1 px-3 h-9 rounded-full border border-navy/15 text-navy text-xs hover:border-terracotta hover:text-terracotta transition"
                        >
                          <Pencil size={12} />
                          {lang === "fr" ? "Modifier" : "Edit"}
                        </button>
                        <button
                          onClick={() => deleteEvent(ev)}
                          data-testid={`admin-event-delete-${ev.event_id}`}
                          className="inline-flex items-center gap-1 px-3 h-9 rounded-full border border-terracotta/30 text-terracotta text-xs hover:bg-terracotta hover:text-alabaster transition"
                        >
                          <Trash2 size={12} />
                          {lang === "fr" ? "Supprimer" : "Delete"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {editingEvent && (
              <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={closeEventEditor}>
                <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-event-modal">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta">{editingEvent === "new" ? (lang === "fr" ? "Nouveau" : "New") : (lang === "fr" ? "Modifier" : "Edit")}</p>
                      <h3 className="font-serif text-2xl text-navy">{lang === "fr" ? "Événement" : "Event"}</h3>
                    </div>
                    <button onClick={closeEventEditor} className="text-navy/50 hover:text-terracotta"><X size={20} /></button>
                  </div>
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">Titre FR *</label>
                        <input value={eventForm.title_fr} onChange={(e) => setEventForm({...eventForm, title_fr: e.target.value})} data-testid="admin-event-title-fr" className="w-full h-10 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm" />
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">Title EN *</label>
                        <input value={eventForm.title_en} onChange={(e) => setEventForm({...eventForm, title_en: e.target.value})} data-testid="admin-event-title-en" className="w-full h-10 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">Description FR</label>
                        <textarea value={eventForm.description_fr} onChange={(e) => setEventForm({...eventForm, description_fr: e.target.value})} rows={3} className="w-full px-3 py-2 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm resize-none" />
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">Description EN</label>
                        <textarea value={eventForm.description_en} onChange={(e) => setEventForm({...eventForm, description_en: e.target.value})} rows={3} className="w-full px-3 py-2 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm resize-none" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div className="sm:col-span-2">
                        <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">{lang === "fr" ? "Date & heure (votre fuseau)" : "Date & time (your timezone)"} *</label>
                        <input type="datetime-local" value={eventForm.date} onChange={(e) => setEventForm({...eventForm, date: e.target.value})} data-testid="admin-event-date" className="w-full h-10 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm" />
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">{lang === "fr" ? "Durée (min)" : "Duration (min)"}</label>
                        <input type="number" value={eventForm.duration_minutes} onChange={(e) => setEventForm({...eventForm, duration_minutes: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">{lang === "fr" ? "Places" : "Capacity"}</label>
                        <input type="number" value={eventForm.capacity} onChange={(e) => setEventForm({...eventForm, capacity: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm" />
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">{lang === "fr" ? "Prix (CAD)" : "Price (CAD)"}</label>
                        <input type="number" step="0.01" value={eventForm.price} onChange={(e) => setEventForm({...eventForm, price: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm" />
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">Type</label>
                        <select value={eventForm.kind} onChange={(e) => setEventForm({...eventForm, kind: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm bg-white">
                          <option value="webinar">Webinar</option>
                          <option value="workshop">Workshop</option>
                          <option value="qa">Q&A</option>
                          <option value="meetup">Meetup</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-1">{lang === "fr" ? "Lien de la rencontre (Zoom/Meet)" : "Meeting link (Zoom/Meet)"}</label>
                      <input value={eventForm.meeting_link} onChange={(e) => setEventForm({...eventForm, meeting_link: e.target.value})} placeholder="https://..." className="w-full h-10 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm" />
                      <p className="text-[10px] text-navy/50 mt-1">
                        {lang === "fr" ? "Envoyé aux inscrits dans l'email de confirmation." : "Sent to registrants in the confirmation email."}
                      </p>
                    </div>
                  </div>
                  <div className="mt-6 flex gap-3 justify-end">
                    <button onClick={closeEventEditor} className="px-5 h-10 rounded-full border border-navy/15 text-navy text-sm hover:border-terracotta transition">
                      {lang === "fr" ? "Annuler" : "Cancel"}
                    </button>
                    <button onClick={saveEvent} disabled={eventSaving} data-testid="admin-event-save" className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-navy text-alabaster hover:bg-terracotta text-sm transition disabled:opacity-40">
                      <Save size={14} />
                      {eventSaving ? (lang === "fr" ? "Enregistrement…" : "Saving…") : (lang === "fr" ? "Enregistrer" : "Save")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* MESSAGING CENTRE */}
        {active === "messages" && (
          <MessagingCenter lang={lang} initialRecipient={messageTarget} />
        )}

        {/* SETTINGS */}
        {active === "settings" && (
          <div className="space-y-8 max-w-3xl">
            {/* IRCC EXPRESS ENTRY SYNC CARD */}
            <div className="bg-white border border-navy/8 rounded-2xl p-8" data-testid="admin-ircc-sync-panel">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-2">
                    {lang === "fr" ? "Données Officielles · Entrée Express" : "Official Data · Express Entry"}
                  </p>
                  <h3 className="font-serif text-2xl text-navy leading-tight">
                    {lang === "fr" ? "Synchronisation automatique IRCC" : "Automatic IRCC Synchronization"}
                  </h3>
                  <p className="text-sm text-navy/60 mt-2 max-w-lg">
                    {lang === "fr"
                      ? "Aspirateur de données direct depuis le site officiel d'IRCC (Canada.ca). Met à jour les scores CRS, dates, catégories et nombre d'invitations en temps réel."
                      : "Direct data scraper from official IRCC portal (Canada.ca). Updates CRS cutoff scores, dates, categories and invitations in real-time."}
                  </p>
                </div>
                <button
                  onClick={syncIRCC}
                  disabled={irccSyncing}
                  data-testid="admin-ircc-sync-btn"
                  className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-terracotta text-white hover:bg-terracotta/90 text-sm font-semibold transition disabled:opacity-50 shrink-0 shadow-sm"
                >
                  <RefreshCw size={15} className={irccSyncing ? "animate-spin" : ""} />
                  <span>
                    {irccSyncing
                      ? (lang === "fr" ? "Synchronisation en cours…" : "Syncing now…")
                      : (lang === "fr" ? "Synchroniser les tirages" : "Sync IRCC Draws")}
                  </span>
                </button>
              </div>

              {irccStatus && (
                <div className="bg-alabaster/60 rounded-xl p-4 border border-navy/5 text-xs text-navy/70 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className="font-semibold text-navy">
                      {lang === "fr" ? "Tirages enregistrés en base : " : "Draws in database: "}
                    </span>
                    <span className="font-mono font-bold text-terracotta">{irccStatus.total_draws || 0}</span>
                  </div>
                  {irccStatus.last_ircc_sync && (
                    <div>
                      <span className="text-navy/50">{lang === "fr" ? "Dernière synchro : " : "Last sync: "}</span>
                      <span className="font-medium text-navy">
                        {new Date(irccStatus.last_ircc_sync).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {irccSyncResult && (
                <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                  <span>
                    {lang === "fr"
                      ? `✓ ${irccSyncResult.synced_count} tirages récupérés et synchronisés avec succès depuis IRCC.`
                      : `✓ ${irccSyncResult.synced_count} draws successfully fetched and synchronized from IRCC.`}
                  </span>
                </div>
              )}
            </div>

            {/* HOMEPAGE VIDEO CARD */}
            <div className="bg-white border border-navy/8 rounded-2xl p-8" data-testid="admin-settings-panel">
              <div className="mb-8">
                <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-2">Homepage · YouTube</p>
                <h3 className="font-serif text-2xl text-navy leading-tight">
                  {lang === "fr" ? "Vidéo d'introduction bilingue" : "Bilingual intro video"}
                </h3>
                <p className="text-sm text-navy/60 mt-2 max-w-lg">
                  {lang === "fr"
                    ? "Collez l'URL YouTube complète ou l'ID vidéo. La vidéo apparaîtra sur la page d'accueil selon la langue choisie par le visiteur."
                    : "Paste the full YouTube URL or the video ID. The video appears on the homepage based on the visitor's chosen language."}
                </p>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-2">
                    {lang === "fr" ? "Vidéo — Français" : "Video — French"}
                  </label>
                  <input
                    value={settings.homepage_video_fr}
                    onChange={(e) => setSettings((s) => ({ ...s, homepage_video_fr: e.target.value }))}
                    placeholder="https://www.youtube.com/watch?v=..."
                    data-testid="admin-video-fr"
                    className="w-full h-11 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-navy/50 block mb-2">
                    {lang === "fr" ? "Vidéo — Anglais" : "Video — English"}
                  </label>
                  <input
                    value={settings.homepage_video_en}
                    onChange={(e) => setSettings((s) => ({ ...s, homepage_video_en: e.target.value }))}
                    placeholder="https://www.youtube.com/watch?v=..."
                    data-testid="admin-video-en"
                    className="w-full h-11 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm"
                  />
                </div>
                <button
                  onClick={saveSettings}
                  disabled={settingsSaving}
                  data-testid="admin-settings-save"
                  className="inline-flex items-center gap-2 px-6 h-11 rounded-full bg-navy text-alabaster hover:bg-terracotta text-sm transition disabled:opacity-40"
                >
                  <Save size={14} />
                  {settingsSaving ? (lang === "fr" ? "Enregistrement…" : "Saving…") : (lang === "fr" ? "Enregistrer" : "Save")}
                </button>
                {settingsSaved && (
                  <p className="text-xs text-terracotta" data-testid="admin-settings-saved">
                    {lang === "fr" ? "✓ Enregistré." : "✓ Saved."}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
          </div>
        </div>
      </section>
    </div>
  );
}
